import type * as React from "react";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon, type IconName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import { useDrawerInsetPanel } from "../../components/ui/drawer";
import { useEntitySelector } from "./entity-selector-context";

// In a sheet the list sits inside the sheet body's 16px inset
// (`DrawerInsetPanel` — the drawer variant's own, or the popover or submenu
// sheet an inline mount sits in); the footer pulls back out to the sheet's
// edges so its hairline spans the sheet, and re-adds that inset to its own
// so its content keeps the list's left edge.
const DRAWER_BLEED = "-mx-4 px-5.5";

export type EntitySelectorFooterProps = Omittable<{
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}>;

/**
 * The popup's closing row, under a top hairline. Its 6px inset is the edge the
 * search well and the row washes share (see entity-selector-layout), so a
 * full-width action's wash lines up with the washes above it and a control —
 * a segmented switch — is framed the way the search well is.
 */
function EntitySelectorFooter({ children, ref }: EntitySelectorFooterProps) {
  const { variant } = useEntitySelector();
  const inSheet = useDrawerInsetPanel();
  return (
    <Box
      border="top"
      className={cn("p-1.5", (variant === "drawer" || inSheet) && DRAWER_BLEED)}
      ref={ref}
      shrink={false}
    >
      {children}
    </Box>
  );
}

EntitySelectorFooter.displayName = "EntitySelectorFooter";

export type EntitySelectorFooterActionProps = Omittable<{
  children: React.ReactNode;
  /** Leading glyph — a create action wears "plus". An action with a glyph
   *  aligns to the start, so the icon and label sit where the list rows'
   *  leading content sits; without one the label stays centered. */
  icon?: IconName;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  ref?: React.Ref<HTMLButtonElement>;
}>;

/**
 * The footer's action row: the escape hatch for what the search box could not
 * find — create the thing you just typed, reset what you changed. Quiet ink,
 * because it answers a search that failed rather than competing with the rows
 * that succeeded.
 *
 * It spans the footer, so it is squared: a stadium filling a full-width slot
 * reads as a control floating over the region instead of the region's own
 * closing row. It stands on the list's one 32px rung — the search well, the
 * section labels and every pool row are 32px, and a 28px closing row under
 * them reads as a different control rather than the list's last line.
 */
function EntitySelectorFooterAction({
  children,
  icon,
  onClick,
  ref,
}: EntitySelectorFooterActionProps) {
  return (
    <Button
      justify={icon ? "start" : undefined}
      fullWidth
      onClick={onClick}
      ref={ref}
      shape="square"
      size="default"
      variant="ghost"
    >
      {icon ? <Icon name={icon} /> : null}
      <Text size="sm" variant="muted">
        {children}
      </Text>
    </Button>
  );
}

EntitySelectorFooterAction.displayName = "EntitySelectorFooterAction";

export { EntitySelectorFooter, EntitySelectorFooterAction };
